import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, CheckCircle2, TrendingUp, Download } from "lucide-react";

// Fictional / generic data — NO real user data is ever shown here.
// Names are common backpacker first names; cities are popular AU job hubs.
const NAMES = [
  "Tom", "Léa", "Marc", "Sophie", "Lena", "Jules", "Anna", "Lukas",
  "Emma", "Felix", "Clara", "Pablo", "Sofia", "Mateo", "Marco", "Giulia",
  "Diego", "Ana", "Hugo", "Mia", "Liam", "Chloé",
];

const CITIES = [
  "Perth", "Karratha", "Newman", "Port Hedland", "Kalgoorlie",
  "Sydney", "Melbourne", "Brisbane", "Cairns", "Mackay",
  "Darwin", "Broome", "Townsville", "Gold Coast",
];

const FLAGS = ["🇫🇷", "🇩🇪", "🇮🇹", "🇪🇸", "🇵🇹", "🇬🇧", "🇦🇷", "🇧🇷", "🇨🇱"];

// One of 4 message templates per locale. {name}, {city}, {score}, {time}
const TEMPLATES = {
  en: [
    { icon: "sparkle", text: "{name} just optimized their CV in {city}" },
    { icon: "download", text: "{name} downloaded their AU-ready CV" },
    { icon: "trend", text: "{name} hit an ATS score of {score}/100" },
    { icon: "check", text: "A backpacker in {city} just landed an interview" },
  ],
  fr: [
    { icon: "sparkle", text: "{name} vient d'optimiser son CV à {city}" },
    { icon: "download", text: "{name} a téléchargé son CV Australien" },
    { icon: "trend", text: "{name} a atteint un score ATS de {score}/100" },
    { icon: "check", text: "Un backpacker à {city} vient de décrocher un entretien" },
  ],
  de: [
    { icon: "sparkle", text: "{name} hat gerade seinen Lebenslauf in {city} optimiert" },
    { icon: "download", text: "{name} hat seinen AU-Lebenslauf heruntergeladen" },
    { icon: "trend", text: "{name} hat einen ATS-Score von {score}/100 erreicht" },
    { icon: "check", text: "Ein Backpacker in {city} hat ein Vorstellungsgespräch bekommen" },
  ],
  it: [
    { icon: "sparkle", text: "{name} ha appena ottimizzato il CV a {city}" },
    { icon: "download", text: "{name} ha scaricato il suo CV australiano" },
    { icon: "trend", text: "{name} ha raggiunto un punteggio ATS di {score}/100" },
    { icon: "check", text: "Un backpacker a {city} ha appena ottenuto un colloquio" },
  ],
  es: [
    { icon: "sparkle", text: "{name} acaba de optimizar su CV en {city}" },
    { icon: "download", text: "{name} ha descargado su CV australiano" },
    { icon: "trend", text: "{name} alcanzó un puntaje ATS de {score}/100" },
    { icon: "check", text: "Un backpacker en {city} acaba de conseguir una entrevista" },
  ],
  pt: [
    { icon: "sparkle", text: "{name} acabou de otimizar o currículo em {city}" },
    { icon: "download", text: "{name} baixou seu CV australiano" },
    { icon: "trend", text: "{name} atingiu uma pontuação ATS de {score}/100" },
    { icon: "check", text: "Um backpacker em {city} conseguiu uma entrevista" },
  ],
};

const TIME_LABELS = {
  en: { just_now: "just now", min_ago: "min ago" },
  fr: { just_now: "à l'instant", min_ago: "min" },
  de: { just_now: "gerade eben", min_ago: "Min." },
  it: { just_now: "proprio ora", min_ago: "min fa" },
  es: { just_now: "ahora mismo", min_ago: "min" },
  pt: { just_now: "agora mesmo", min_ago: "min" },
};

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function buildNotification(lang) {
  const locale = TEMPLATES[lang] ? lang : "en";
  const tpl = pick(TEMPLATES[locale]);
  const name = pick(NAMES);
  const city = pick(CITIES);
  const flag = pick(FLAGS);
  const score = 86 + Math.floor(Math.random() * 12); // 86-97
  const minutes = Math.floor(Math.random() * 9); // 0-8
  const labels = TIME_LABELS[locale];
  const time = minutes === 0 ? labels.just_now : `${minutes} ${labels.min_ago}`;
  const text = tpl.text
    .replace("{name}", name)
    .replace("{city}", city)
    .replace("{score}", String(score));
  return { icon: tpl.icon, text, flag, time, id: Date.now() + Math.random() };
}

const ICONS = {
  sparkle: Sparkles,
  download: Download,
  trend: TrendingUp,
  check: CheckCircle2,
};

const ICON_COLORS = {
  sparkle: "#D95D39",  // terracotta
  download: "#2A5C5A", // eucalyptus
  trend: "#2A5C5A",
  check: "#2A5C5A",
};

export default function SocialProofToast({ lang = "en" }) {
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timeouts = [];
    let mounted = true;

    const showOne = () => {
      if (!mounted) return;
      setCurrent(buildNotification(lang));
      setVisible(true);
      // Hide after 5s
      timeouts.push(setTimeout(() => {
        if (!mounted) return;
        setVisible(false);
        // Schedule next after 9-13s pause
        const pause = 9000 + Math.random() * 4000;
        timeouts.push(setTimeout(showOne, pause));
      }, 5200));
    };

    // First toast after 8s (let user see the hero first)
    timeouts.push(setTimeout(showOne, 8000));

    return () => {
      mounted = false;
      timeouts.forEach(clearTimeout);
    };
  }, [lang]);

  const Icon = useMemo(() => (current ? ICONS[current.icon] || Sparkles : Sparkles), [current]);
  const iconColor = useMemo(() => (current ? ICON_COLORS[current.icon] || "#D95D39" : "#D95D39"), [current]);

  return (
    <div
      className="fixed bottom-5 left-4 sm:left-6 z-[60] pointer-events-none"
      data-testid="social-proof-toast-container"
    >
      <AnimatePresence mode="wait">
        {visible && current && (
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: -40, y: 10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: -20, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="pointer-events-auto max-w-[320px] sm:max-w-[360px]"
            data-testid="social-proof-toast"
          >
            <div className="flex items-center gap-3 bg-white/95 backdrop-blur-md border border-[#E7E2D5] shadow-[0_10px_30px_-10px_rgba(41,37,36,0.25)] rounded-2xl pl-3 pr-4 py-3">
              <div
                className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${iconColor}15` }}
              >
                <Icon className="w-4.5 h-4.5" style={{ color: iconColor }} strokeWidth={2.2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-base leading-none" aria-hidden="true">{current.flag}</span>
                  <p className="text-[13px] sm:text-sm font-medium text-[#292524] leading-snug truncate">
                    {current.text}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#2A5C5A] animate-pulse" />
                  <span className="text-[11px] text-[#78716C] font-medium">{current.time}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
